import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWebSocket } from "../../contexts/WebSocketProvider";
import {
    getStatusIcon,
    getStatusLabel,
    useCDDFilters
} from "../../hooks/useCDDFilters";
import { fetchCDDRecords, type CaseListCompleteness } from "../../services/configService";
import useCddRecordsStore from "../../stores/cddRecordsStore";
import "./CDDRecords.css";
import PortalHeader from "./PortalHeader";
// AL-455: per-person case progress.
import CheckAccountsPanel from "../cdd-records/sections/shared/CheckAccountsPanel";
import { OutstandingCount } from "./OutstandingPeople";

const CDDRecords: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // AL-455: which case's per-person detail is open.
  const [progressCase, setProgressCase] = React.useState<{ id: string; name: string } | null>(null);

  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  // Remembers what opened the dialog, so focus can be handed back on close.
  const openerRef = React.useRef<HTMLElement | null>(null);

  /**
   * AL-455: keyboard behaviour for the dialog.
   *
   * Escape closes it — the backdrop click already did, but a dialog dismissable
   * only with a mouse is unusable from the keyboard.
   *
   * Tab is trapped inside it. Without this, tabbing walks out of the dialog and
   * into the table behind, which is still rendered and still focusable, leaving
   * a keyboard user typing into content that is visually obscured.
   *
   * Focus moves in on open and returns to the chip that opened it on close, so
   * position in a long list is not lost.
   */
  React.useEffect(() => {
    if (!progressCase) {
      // Hand focus back to whatever opened the dialog.
      openerRef.current?.focus?.();
      openerRef.current = null;
      return;
    }

    openerRef.current = document.activeElement as HTMLElement | null;
    // Focus the dialog itself rather than its first control: the panel loads
    // asynchronously, so the first control does not exist yet on open.
    dialogRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProgressCase(null);
        return;
      }
      if (e.key !== "Tab") return;

      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [progressCase]);

  /**
   * AL-455: "2 of 3 outstanding" on the row, expanding to who is holding it up.
   *
   * Deliberately keyed on the backend's counts rather than on record.status. The status bucket is not a
   * reliable gate here — normalizeCDDStatus falls back to 'pending', so cases that are plainly still in
   * flight do not carry 'in-progress'. If the server says people are outstanding, say so, whatever the
   * status column happens to read.
   *
   * Absent counts render nothing at all. The backend omits them when the rollup could not be computed,
   * and "0 outstanding" against an unknown value would be a false all-clear.
   */
  /**
   * AL-455: the outstanding count, on a second line in the Status cell. Returns
   * null when nobody is outstanding or the counts are absent, so the cell is
   * unchanged for those rows.
   */
  const renderOutstandingCount = (record: any) => (
    <OutstandingCount
      recordId={record.id}
      totalPeople={record.totalPeople}
      outstandingPeople={record.outstandingPeople}
      onClick={() =>
        setProgressCase({ id: record.id, name: record.customer || record.reference })
      }
    />
  );
  const { isAuthenticated: wsAuthenticated } = useWebSocket();

  /**
   * AL-511: whether this list is the whole set.
   *
   * Starts as "unknown" rather than "complete", so nothing is claimed before the
   * first reply arrives — and nothing is claimed at all against a server that
   * does not report it, which includes live today.
   */
  const [completeness, setCompleteness] = React.useState<CaseListCompleteness>({
    shown: 0,
    truncated: false,
    isKnown: false,
  });

  const storeRecords = useCddRecordsStore((state) => state.allRecords);
  const isLoading = useCddRecordsStore((state) => state.isLoading);
  const setLoading = useCddRecordsStore((state) => state.setLoading);
  const setRecords = useCddRecordsStore((state) => state.setRecords);

  const {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    riskFilter,
    setRiskFilter,
    appliedFilters,
    setAppliedFilters,
    applyFilters,
    resetFilters,
    getFilteredData,
    getPaginatedData,
    getTotalPages,
    currentPage,
    setCurrentPage,
  } = useCDDFilters();

  // Refresh whenever this authenticated route is entered, even if Zustand has cached records.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!wsAuthenticated || hasFetchedRef.current || isLoading) return;
    hasFetchedRef.current = true;
    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchCDDRecords();
        setRecords(result.data);
        // AL-511: only remembered so the list can admit to being incomplete.
        setCompleteness(result.completeness);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [wsAuthenticated, isLoading, setLoading, setRecords]);

  // Apply URL query parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next: typeof appliedFilters = {
      search: params.get("search") || "",
      status: (params.get("status") as any) || "all",
      type: (params.get("type") as any) || "all",
      risk: (params.get("risk") as any) || "all",
    };
    if (params.get("search")) setSearchTerm(next.search);
    if (params.get("status")) setStatusFilter(next.status);
    if (params.get("type")) setTypeFilter(next.type as any);
    if (params.get("risk")) setRiskFilter(next.risk as any);
    setAppliedFilters(next);
    setCurrentPage(1);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredData = getFilteredData(storeRecords as any);

  // Virtualisation
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredData.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - virtualItems[virtualItems.length - 1].end
      : 0;

  return (
    <div className="portal-container">
      <PortalHeader onLogout={() => navigate("/login")} />

      <main className="portal-content">
        <div className="relative pl-5 mb-8">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full" />
          <h1>Your CDD Records</h1>
          <p style={{ color: "#475569", fontSize: 14, margin: 0, fontWeight: 500 }}>
            View and manage all customer due diligence records across your portfolio.
          </p>
        </div>

        <div className="filter-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search by name, ABN, reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-controls">
            <div className="filter-group">
              <label>Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">▼ All</option>
                <option value="not-started">⚪ Not Started</option>
                <option value="pending">⏳ Pending</option>
                <option value="in-progress">🔄 In Progress</option>
                <option value="completed">✅ Completed</option>
                <option value="failed">❌ Withdrawn</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Type:</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
              >
                <option value="all">▼ All</option>
                <option value="Company">Company</option>
                <option value="Trust">Trust</option>
                <option value="Association">Association</option>
                <option value="Reg. Body">Reg. Body</option>
                <option value="Individual">Individual</option>
              </select>
            </div>

          </div>

          <div className="filter-actions">
            <button
              className="btn-primary"
              data-testid="apply-filters-btn"
              onClick={applyFilters}
            >
              Apply Filters
            </button>
            <button
              className="btn-secondary"
              data-testid="clear-filters-btn"
              onClick={resetFilters}
            >
              Clear
            </button>
            <button className="btn-export" data-testid="export-csv-btn">
              📥 Export CSV
            </button>
          </div>
        </div>

        <div className="records-table-section">
          {isLoading ? (
            <p className="record-count">Loading records…</p>
          ) : (
            <p className="record-count">
              {filteredData.length} record{filteredData.length !== 1 ? "s" : ""}
            </p>
          )}

          {/*
            AL-511: the list used to present itself as the whole set. The server
            caps at 500 and reports the real total, so when the cap bites the
            reader is told rather than left to compare against the dashboard.

            Rendered ONLY when the server positively reported truncation. An
            older server sends no total — live today — and a status filter makes
            the server withhold it on purpose, because the filter runs after the
            SQL LIMIT and a pre-limit count would over-report. In both cases this
            stays silent: an absent total is unknown, not an all-clear.
          */}
          {!isLoading && completeness.isKnown && completeness.truncated && (
            <p className="record-count-truncated" role="status" data-testid="records-truncated">
              Showing {completeness.shown} of {completeness.totalAvailable} cases.
              The oldest are not listed — this view is capped, so filter or search
              to reach the rest.
            </p>
          )}

          <div
            ref={scrollContainerRef}
            className="table-wrapper"
            style={{ maxHeight: filteredData.length > 15 ? 720 : undefined, overflow: filteredData.length > 15 ? "auto" : undefined }}
          >
            <table className="records-table" data-testid="records-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Customer</th>
                  <th>ABN</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="no-results">
                      {isLoading ? "Loading…" : "No records match your filters."}
                    </td>
                  </tr>
                ) : filteredData.length <= 15 ? (
                  filteredData.map((record, idx) => (
                    <tr
                      key={record.id || idx}
                      className={`status-${record.status}`}
                      data-status={record.status}
                    >
                      <td className="ref-cell" data-label="Reference">{record.reference}</td>
                      <td className="customer-cell" data-label="Customer">{record.customer}</td>
                      <td className="abn-cell" data-label="ABN">{record.abn || "—"}</td>
                      <td className="type-cell" data-label="Type">
                        <span className="type-badge">{record.type}</span>
                      </td>
                      <td className="status-cell" data-label="Status">
                        <span className="status-badge">
                          {getStatusIcon(record.status)}{" "}
                          {getStatusLabel(record.status)}
                        </span>
                        {/* AL-455: a second line in this cell rather than its own column. The
                            table is already six columns and tight, and a count in one column
                            plus a link in the Action cell said the same thing twice. Below the
                            badge rather than appended to it, so there is room for the word
                            "outstanding" — a bare "2 of 3" beside a status reads as a step
                            counter. Costs a row of height, no width. */}
                        {renderOutstandingCount(record)}
                      </td>
                      {/*
                        AL-455: this cell holds actions only, and deliberately no
                        outstanding-people branch.

                        An earlier pass put one here, ahead of the status checks.
                        Outstanding people and a finished case are not mutually
                        exclusive — per AL-456 approved users do not hold a case up,
                        so a completed case can still carry people who never logged
                        in — and that branch therefore replaced [View] on completed
                        rows, leaving a finished report with no way to open it.

                        Keeping the count in the Status cell and actions here means
                        the two cannot compete for the same slot. The wording is
                        changed from "Processing…" only because
                        normalizeCDDStatus maps data_collection,
                        checks_in_progress and remediation all to 'in-progress',
                        and no report is being generated in any of them.
                      */}
                      <td className="action-cell">
                        {record.status === "in-progress" ? (
                          <span
                            className="btn-processing"
                            title="The report cannot be opened until the case completes."
                          >
                            Not yet available
                          </span>
                        ) : record.status === "failed" ? (
                          <span
                            className="btn-unavailable"
                            title="Withdrawn records cannot be opened"
                          >
                            Unavailable
                          </span>
                        ) : (
                          <button
                            className="btn-view"
                            onClick={() => navigate(`/portal/cdd-records/${record.id}`)}
                            data-testid={`btn-view-record-${record.id}`}
                          >
                            [View]
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <>
                    {paddingTop > 0 && (
                      <tr><td colSpan={6} style={{ height: paddingTop, padding: 0, border: "none" }} /></tr>
                    )}
                    {virtualItems.map((virtualRow) => {
                      const record = filteredData[virtualRow.index];
                      if (!record) return null;
                      return (
                        <tr
                          key={record.id || virtualRow.index}
                          className={`status-${record.status}`}
                          data-status={record.status}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                        >
                          <td className="ref-cell" data-label="Reference">{record.reference}</td>
                          <td className="customer-cell" data-label="Customer">{record.customer}</td>
                          <td className="abn-cell" data-label="ABN">{record.abn || "—"}</td>
                          <td className="type-cell" data-label="Type">
                            <span className="type-badge">{record.type}</span>
                          </td>
                          <td className="status-cell" data-label="Status">
                            <span className="status-badge">
                              {getStatusIcon(record.status)}{" "}
                              {getStatusLabel(record.status)}
                            </span>
                            {/* AL-455: see the note on the other table above — same cell, same
                                reason. Both renderings go through one helper so they cannot
                                drift apart. */}
                            {renderOutstandingCount(record)}
                          </td>
                          <td className="action-cell">
                            {record.status === "in-progress" ? (
                              <span
                                className="btn-processing"
                                title="The report cannot be opened until the case completes."
                              >
                                Not yet available
                              </span>
                            ) : record.status === "failed" ? (
                              <span
                                className="btn-unavailable"
                                title="Withdrawn records cannot be opened"
                              >
                                Unavailable
                              </span>
                            ) : (
                              <button
                                className="btn-view"
                                onClick={() =>
                                  navigate(`/portal/cdd-records/${record.id}`)
                                }
                                data-testid={`btn-view-record-${record.id}`}
                              >
                                [View]
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {paddingBottom > 0 && (
                      <tr><td colSpan={6} style={{ height: paddingBottom, padding: 0, border: "none" }} /></tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="records-info">
          <p>
            ℹ️ Click row to view details. Completed = view cert & profile. In
            Progress = limited info. Withdrawn = see withdrawal reason.
          </p>
        </div>
      </main>

      {/*
        AL-455: who is holding this case up.

        A modal rather than an expanding row, deliberately. This table has two render paths and the
        virtualised one measures row heights via rowVirtualizer.measureElement, so an inline expander
        would fight the virtualiser. A modal behaves identically in both paths.
      */}
      {progressCase && (
        <div
          className="op-dialog-backdrop"
          onClick={() => setProgressCase(null)}
        >
          {/*
            AL-455: role="dialog" belongs on the panel, not the backdrop. It was
            on the backdrop, which spans the viewport — so the dialog's accessible
            boundary enclosed the entire page rather than the panel, and
            aria-modal claimed the whole screen was modal content.

            tabIndex={-1} so focus can be moved here programmatically on open
            without adding it to the tab order.
          */}
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="op-dialog-title"
            aria-describedby="op-dialog-subtitle"
            tabIndex={-1}
            className="op-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="op-dialog-header">
              <div>
                <h2 id="op-dialog-title" className="op-dialog-title">
                  {progressCase.name}
                </h2>
                <p id="op-dialog-subtitle" className="op-dialog-subtitle">
                  Who this case is waiting on
                </p>
              </div>
              <button
                type="button"
                onClick={() => setProgressCase(null)}
                aria-label="Close"
                className="op-dialog-close"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <CheckAccountsPanel caseIdentifier={progressCase.id} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CDDRecords;
